/**
 * Detail einer Abteilung mit Tabs: Allgemein, Mitglieder, Stages, Dashboard, Rollen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card } from '../../../components/ui';
import { departmentsApi, type DepartmentDetail, type RbacRole } from '../../../lib/departments';
import { BRAND, getThemeColors } from '../../../lib/theme';
import type { Density } from '../../../types';
import { groupKey } from './shared';
import { DepartmentGeneralTab } from './DepartmentGeneralTab';
import { DepartmentMembersTab } from './DepartmentMembersTab';
import { DepartmentStagesTab } from './DepartmentStagesTab';
import { DepartmentDashboardTab } from './DepartmentDashboardTab';
import { DepartmentRolesTab } from './DepartmentRolesTab';

type TabKey = 'general' | 'members' | 'stages' | 'dashboard' | 'roles';
const TABS: TabKey[] = ['general', 'members', 'stages', 'dashboard', 'roles'];

interface Props {
  dark: boolean;
  departmentId: number;
  roles: RbacRole[];
  onBack: () => void;
  /** Etwas wurde gespeichert (Liste und Menü neu laden). */
  onChanged: () => void;
  onOpenRoles?: () => void;
  density?: Density;
}

export function AbteilungDetail({ dark, departmentId, roles, onBack, onChanged, onOpenRoles, density = 'comfortable' }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [tab, setTab] = useState<TabKey>('general');
  const [dept, setDept] = useState<DepartmentDetail | null>(null);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    try {
      setDept(await departmentsApi.get(departmentId));
      setError(false);
    } catch {
      setError(true);
    }
  }, [departmentId]);

  useEffect(() => { void reload(); }, [reload]);

  const saved = useCallback(async () => { await reload(); onChanged(); }, [reload, onChanged]);

  if (error) {
    return (
      <Card dark={dark} style={{ padding: 20 }}>
        <div role="alert" style={{ color: '#b42318', fontSize: 13, marginBottom: 12 }}>{tr('departments.detail.load_error')}</div>
        <Button dark={dark} variant="secondary" size="sm" onClick={onBack}>{tr('departments.detail.back')}</Button>
      </Card>
    );
  }
  if (!dept) return <div style={{ color: t.muted, fontSize: 13 }}>{tr('departments.page.loading')}</div>;

  const count = (k: TabKey): number | null =>
    k === 'members' ? dept.members.length : k === 'stages' ? dept.stages.length : k === 'roles' ? dept.role_ids.length : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <button
          type="button"
          onClick={onBack}
          style={{ background: 'none', border: 'none', color: BRAND, cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: 'Outfit, sans-serif' }}
        >
          {tr('departments.detail.breadcrumb')}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
          <span aria-hidden style={{ width: 14, height: 14, borderRadius: 4, background: dept.color }}/>
          <span style={{ fontSize: 18, fontWeight: 700, color: t.heading }}>{dept.name}</span>
          <Badge dark={dark} tone="brand">{tr(`departments.group.${groupKey(dept.user_role)}_short`)}</Badge>
          <Badge dark={dark} tone={dept.is_active ? 'success' : 'warning'}>
            {dept.is_active ? tr('departments.settings.active') : tr('departments.settings.inactive')}
          </Badge>
        </div>
      </div>

      <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${t.border}`, overflowX: 'auto' }}>
        {TABS.map(k => {
          const active = tab === k;
          const c = count(k);
          return (
            <button
              key={k}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(k)}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer', padding: '9px 14px',
                fontSize: 13, fontWeight: active ? 600 : 500, color: active ? BRAND : t.text,
                borderBottom: `2px solid ${active ? BRAND : 'transparent'}`, marginBottom: -1,
                fontFamily: 'Outfit, sans-serif', whiteSpace: 'nowrap',
              }}
            >
              {tr(`departments.tabs.${k}`)}{c != null ? ` (${c})` : ''}
            </button>
          );
        })}
      </div>

      {tab === 'general' && <DepartmentGeneralTab dark={dark} dept={dept} onSaved={saved} />}
      {tab === 'members' && <DepartmentMembersTab dark={dark} dept={dept} onSaved={saved} />}
      {tab === 'stages' && <DepartmentStagesTab dark={dark} dept={dept} onSaved={saved} />}
      {tab === 'dashboard' && <DepartmentDashboardTab dark={dark} density={density} dept={dept} onSaved={saved} />}
      {tab === 'roles' && <DepartmentRolesTab dark={dark} dept={dept} roles={roles} onSaved={saved} onOpenRoles={onOpenRoles} />}
    </div>
  );
}
